import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Landmark, Loader2, TrendingUp, Users, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email.trim(), password);
      toast.success(`Welcome back, ${u.name}`);
      navigate("/");
    } catch (err) {
      setError(apiError(err, "Invalid credentials"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-950">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-slate-900 border-r border-slate-800 relative overflow-hidden">
        <div className="absolute -top-24 -right-24 h-96 w-96 rounded-full bg-blue-600/20 blur-3xl" />
        <div className="absolute -bottom-32 -left-16 h-80 w-80 rounded-full bg-indigo-600/10 blur-3xl" />
        <div className="flex items-center gap-3 relative">
          <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center">
            <Landmark className="h-6 w-6 text-white" />
          </div>
          <div>
            <div className="font-heading font-bold text-white text-lg leading-tight">LoanSupportIndia</div>
            <div className="text-xs uppercase tracking-[0.25em] text-slate-500">DSA CRM Console</div>
          </div>
        </div>
        <div className="relative">
          <h1 className="font-heading text-4xl font-extrabold text-white leading-[1.1]">
            Your entire loan business,<br /><span className="text-blue-400">now on the web.</span>
          </h1>
          <p className="mt-4 text-slate-400 max-w-md">
            Leads, applications, follow-ups, attendance, payroll and analytics — synced live with your mobile app.
          </p>
          <div className="mt-8 space-y-3">
            {[[TrendingUp, "Real-time pipeline & analytics"], [Users, "Team, telecallers & coordinators"], [ShieldCheck, "Same secure backend as your app"]].map(([Icon, t], i) => (
              <div key={i} className="flex items-center gap-3 text-slate-300 text-sm">
                <div className="h-8 w-8 rounded-lg bg-slate-800 flex items-center justify-center"><Icon className="h-4 w-4 text-blue-400" /></div>
                {t}
              </div>
            ))}
          </div>
        </div>
        <div className="text-xs text-slate-600 relative">© {new Date().getFullYear()} LoanSupportIndia</div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center"><Landmark className="h-5 w-5 text-white" /></div>
            <div className="font-heading font-bold text-white text-lg">LoanSupportIndia</div>
          </div>
          <h2 className="font-heading text-2xl font-bold text-white">Sign in</h2>
          <p className="text-slate-400 text-sm mt-1">Use your LoanSupportIndia account.</p>

          <form onSubmit={submit} className="mt-8 space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-300">Email</Label>
              <Input id="email" type="email" data-testid="login-email-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@loansupportindia.com" autoComplete="username" className="bg-slate-900 border-slate-700 text-white placeholder:text-slate-600 h-11" required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-300">Password</Label>
              <Input id="password" type="password" data-testid="login-password-input" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" autoComplete="current-password" className="bg-slate-900 border-slate-700 text-white placeholder:text-slate-600 h-11" required />
            </div>
            {error && <p className="text-sm text-rose-400 bg-rose-950/40 border border-rose-900 rounded-lg px-3 py-2" data-testid="login-error">{error}</p>}
            <Button type="submit" disabled={loading} data-testid="login-submit-button" className="w-full h-11 bg-blue-600 hover:bg-blue-500 text-white font-semibold">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
